import { Card, CardContent } from '@/components/ui/shadcn/card'
import { Button } from '@/components/ui/shadcn/button'
import { Input } from '@/components/ui/shadcn/input'
import { Label } from '@/components/ui/shadcn/label'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/shadcn/tooltip'
import { Info, AlertCircle } from 'lucide-react'
import {
  COSTS,
  calculateCost,
  calculateTotalCost,
} from '@/config/constants/billing'

const LimitField = ({ label, id, value, onChange, costType }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    <div className="flex items-center gap-2">
      <Input
        id={id}
        type="number"
        min={0}
        value={value || 0}
        onChange={e => onChange(e.target.value)}
      />
      <span className="shrink-0 text-sm text-muted-foreground">
        ${calculateCost(value || 0, costType).toFixed(2)}
      </span>
    </div>
  </div>
)

export default function BillingLimits({
  billingDetails,
  handleChange,
  handleSave,
  saving,
  error,
  getCurrentTierPrice,
  calculateTotalMonthlyCost,
  currentTier,
}) {
  if (!currentTier || currentTier === 'Free') return null

  return (
    <div className="space-y-3">
      <Alert>
        <Info className="size-4" />
        <AlertDescription>
          <div>Additional usage costs beyond tier limits:</div>
          <ul className="mt-2 ml-4 list-disc space-y-0.5">
            <li>Input Tokens: ${COSTS.inputTokens} per million tokens</li>
            <li>Output Tokens: ${COSTS.outputTokens} per million tokens</li>
            <li>Transcription Minutes: ${COSTS.minutes} per 60 minutes</li>
          </ul>
        </AlertDescription>
      </Alert>

      <Card className="gap-3 py-4">
        <CardContent className="px-4 space-y-3">
          <LimitField
            label="Input Token Limit"
            id="input-token-limit"
            value={billingDetails?.inputTokenLimit}
            onChange={v => handleChange('inputTokenLimit', v)}
            costType="inputTokens"
          />
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <LimitField
            label="Output Token Limit"
            id="output-token-limit"
            value={billingDetails?.outputTokenLimit}
            onChange={v => handleChange('outputTokenLimit', v)}
            costType="outputTokens"
          />
          <LimitField
            label="Minute Limit"
            id="minute-limit"
            value={billingDetails?.minuteLimit}
            onChange={v => handleChange('minuteLimit', v)}
            costType="minutes"
          />

          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              Total Custom Maximum Spend Limit:
              <b className="mx-1 text-foreground">
                ${calculateTotalCost(billingDetails).toFixed(2)}/month
              </b>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="size-4" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="space-y-1 text-xs">
                      <p>
                        This is the maximum additional cost you will incur
                        beyond your tier&apos;s base price.
                      </p>
                      <p>
                        Base Tier Cost: ${getCurrentTierPrice().toFixed(2)}
                        /month
                      </p>
                      <p>
                        Total Monthly Cost: $
                        {calculateTotalMonthlyCost().toFixed(2)}
                        /month
                      </p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Limits'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
